import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../Modal';
import DocMarkdown from './DocMarkdown';
import DocEditor from './DocEditor';
import DocHistory from './DocHistory';
import DocPermissions from './DocPermissions';
import { DocIcon } from './DocIcons';
import { parse, toc as tocOf, taskCount } from '../../lib/docsMarkdown';
import { docsApi, errorOf, when, fullDate, TYPE_LABEL, LEVEL_LABEL } from '../../lib/docs';
import { fmtDate } from '../../lib/when';

/**
 * One article, to read - and, for an editor, to write, to look back through
 * and to share.
 *
 * READING is the point, so it gets the room: a measure a person can read, a
 * table of contents beside it (above it on a phone), and for a runbook the
 * steps as boxes to tick while doing the work. The ticks live in this
 * browser tab only (sessionStorage), keyed by the article AND its version -
 * two people can follow one runbook at once, and a new version starts clean.
 * Print gives a clean page: no navigation, no buttons, links spelled out.
 */

const tickKey = (id, v) => `jv-doc-ticks:${id}:v${v}`;
const readTicks = (id, v) => {
  try { return new Set(JSON.parse(sessionStorage.getItem(tickKey(id, v)) || '[]')); } catch { return new Set(); }
};

export function Feedback({ id, mine, onSaved }) {
  const [state, setState] = useState(mine);
  const [note, setNote] = useState(mine?.note || '');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const send = async (helpful, withNote = null) => {
    setBusy(true); setErr(null);
    try {
      const r = await docsApi.post(`/docs/articles/${id}/feedback`, { helpful, note: withNote });
      setState(r.mine); setAsking(!helpful && withNote === null);
      if (onSaved) onSaved(r.mine);
    } catch (e) { setErr(errorOf(e)); }
    setBusy(false);
  };
  return (
    <section className="da-feedback" aria-label="Was this helpful?">
      <span className="da-feedback-q">Was this helpful?</span>
      <div className="da-feedback-btns">
        <button type="button" className={`da-fb${state?.helpful === true ? ' is-on' : ''}`} aria-pressed={state?.helpful === true} disabled={busy} onClick={() => send(true)}>
          <DocIcon name="thumbup" />Yes
        </button>
        <button type="button" className={`da-fb${state?.helpful === false ? ' is-on' : ''}`} aria-pressed={state?.helpful === false} disabled={busy} onClick={() => send(false)}>
          <DocIcon name="thumbdown" />No
        </button>
      </div>
      {state && !asking && <span className="da-feedback-thanks" role="status">Thank you{state.note ? ' - your note was sent to the editors' : ''}.</span>}
      {asking && (
        <div className="da-feedback-note">
          <label htmlFor="da-fb-note">What was missing or wrong? (only editors see this)</label>
          <textarea id="da-fb-note" rows={3} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          <div className="da-row">
            <button type="button" className="btn btn-primary btn-small" disabled={busy || !note.trim()} onClick={() => send(false, note.trim())}>Send</button>
            <button type="button" className="btn btn-plain btn-small" onClick={() => setAsking(false)}>Skip</button>
          </div>
        </div>
      )}
      {err && <span className="dp-error" role="alert">{err}</span>}
    </section>
  );
}

function Comments({ id, canComment }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState('');
  const [err, setErr] = useState(null);
  const load = useCallback(() => docsApi.get(`/docs/articles/${id}/comments`).then((r) => setList(r.comments)).catch(() => setList([])), [id]);
  useEffect(() => { load(); }, [load]);
  const post = async () => {
    setErr(null);
    try { await docsApi.post(`/docs/articles/${id}/comments`, { body: text }); setText(''); load(); } catch (e) { setErr(errorOf(e)); }
  };
  const remove = async (cid) => { try { await docsApi.del(`/docs/comments/${cid}`); load(); } catch (e) { setErr(errorOf(e)); } };
  if (!list) return null;
  if (!list.length && !canComment) return null;
  return (
    <section className="da-comments" aria-label="Comments">
      <h2 className="da-side-title">Comments{list.length ? ` (${list.length})` : ''}</h2>
      <ul>
        {list.map((c) => (
          <li key={c.id} className="da-comment">
            <div className="da-comment-head"><strong>{c.author_name}</strong><time dateTime={c.created_at}>{when(c.created_at)}</time>
              {c.can_delete && <button type="button" className="btn btn-plain btn-small" onClick={() => remove(c.id)}>Remove</button>}</div>
            <p>{c.body}</p>
          </li>
        ))}
      </ul>
      {canComment && (
        <div className="da-comment-form">
          <label htmlFor="da-comment" className="dp-sr">Add a comment</label>
          <textarea id="da-comment" rows={2} value={text} maxLength={4000} placeholder="Add a comment - a correction, a tip, a question"
                    onChange={(e) => setText(e.target.value)} />
          <button type="button" className="btn btn-secondary btn-small" disabled={!text.trim()} onClick={post}>Comment</button>
        </div>
      )}
      {err && <p className="dp-error" role="alert">{err}</p>}
    </section>
  );
}

function Review({ id, review, onChanged, me }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = async (fn) => { setBusy(true); setErr(null); try { await fn(); onChanged(); } catch (e) { setErr(errorOf(e)); } setBusy(false); };
  return (
    <section className={`da-review${review?.overdue ? ' is-due' : ''}`} aria-label="Review">
      <h2 className="da-side-title">Review</h2>
      <p>
        {review?.owner ? <>Owner: <strong>{review.owner.name}</strong></> : 'No owner yet.'}
        {review?.every_days ? <> · every {review.every_days} days{review.due_at && <> · {review.overdue ? 'due since' : 'next'} {fmtDate(review.due_at)}</>}</> : ' · not on a review cycle'}
      </p>
      <div className="da-row da-wrap">
        <select aria-label="Review interval" className="dp-select" value={review?.every_days || ''} disabled={busy}
                onChange={(e) => run(() => docsApi.patch(`/docs/nodes/${id}`, { review_every_days: e.target.value ? Number(e.target.value) : null }))}>
          <option value="">No reviews</option>
          {[30, 90, 180, 365].map((d) => <option key={d} value={d}>Every {d === 365 ? 'year' : `${d} days`}</option>)}
        </select>
        {me?.userId && review?.owner?.id !== me.userId && (
          <button type="button" className="btn btn-plain btn-small" disabled={busy} onClick={() => run(() => docsApi.patch(`/docs/nodes/${id}`, { owner_user_id: me.userId }))}>Make me the owner</button>
        )}
        <button type="button" className="btn btn-secondary btn-small" disabled={busy} onClick={() => run(() => docsApi.post(`/docs/articles/${id}/reviewed`, {}))}>
          <DocIcon name="check" />Still correct
        </button>
      </div>
      {err && <p className="dp-error" role="alert">{err}</p>}
    </section>
  );
}

function Cards({ title, list }) {
  if (!list?.length) return null;
  return (
    <section className="da-related" aria-label={title}>
      <h2 className="da-side-title">{title}</h2>
      <ul>
        {list.map((a) => (
          <li key={a.id}><Link to={`/docs/${a.id}`} className="da-rel"><DocIcon name={a.article_type} /><span>{a.title}<small>{a.type_label}</small></span></Link></li>
        ))}
      </ul>
    </section>
  );
}

export default function DocArticle({ data, mode, setMode, reload, me, onArchived }) {
  const { node, article, can, refs, files, breadcrumbs } = data;
  const blocks = useMemo(() => parse(article.body), [article.body]);
  const toc = useMemo(() => tocOf(blocks).filter((h) => h.level <= 3), [blocks]);
  const steps = useMemo(() => taskCount(blocks), [blocks]);
  const [ticks, setTicks] = useState(() => readTicks(node.id, article.version));
  const [share, setShare] = useState(false);
  const [fav, setFav] = useState(data.favourite);
  const [askArchive, setAskArchive] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setTicks(readTicks(node.id, article.version)); }, [node.id, article.version]);
  useEffect(() => { setFav(data.favourite); }, [data.favourite]);
  // Arriving at #a-heading scrolls there once the text is drawn.
  useEffect(() => {
    if (mode !== 'read' || !window.location.hash) return;
    const el = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
    if (el) setTimeout(() => el.scrollIntoView({ block: 'start' }), 50);
  }, [mode, node.id]);

  const tasks = steps ? {
    checked: ticks,
    toggle: (n) => {
      const next = new Set(ticks);
      if (next.has(n)) next.delete(n); else next.add(n);
      setTicks(next);
      try { sessionStorage.setItem(tickKey(node.id, article.version), JSON.stringify([...next])); } catch { /* a private window */ }
    }
  } : null;

  const toggleFav = async () => {
    try {
      if (fav) await docsApi.del(`/docs/nodes/${node.id}/favourite`); else await docsApi.put(`/docs/nodes/${node.id}/favourite`);
      setFav(!fav);
    } catch (e) { setErr(errorOf(e)); }
  };
  const archive = async () => {
    try { await docsApi.del(`/docs/nodes/${node.id}`); onArchived(breadcrumbs[breadcrumbs.length - 2]?.id); } catch (e) { setErr(errorOf(e)); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href.split('?')[0]); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };

  if (mode === 'edit' && can.edit) {
    return (
      <DocEditor id={node.id} article={article}
                 onSaved={() => reload()}
                 onClose={(reloadFirst) => { setMode('read'); if (reloadFirst) reload(); }} />
    );
  }

  const status = article.status;
  return (
    <article className={`da da-type-${article.article_type}`}>
      <header className="da-head">
        <div className="da-kicker">
          <span className={`da-type da-type-${article.article_type}`}><DocIcon name={article.article_type} />{TYPE_LABEL[article.article_type]}</span>
          {can.edit && status !== 'published' && (
            <span className={`da-status da-status-${status}`}>{status === 'draft' ? 'Draft - not published' : `Unpublished changes (readers see v${article.published_version})`}</span>
          )}
          {can.edit && article.showing === 'latest' && status === 'published' && <span className="da-status da-status-published">Published</span>}
        </div>
        <h1 className="da-title">{article.title}</h1>
        <p className="da-byline">
          Updated {when(article.updated_at)} by {article.author_name || 'somebody'}
          {can.edit && <> · version {article.version}</>}
          {data.views && <> · read {data.views.total} time{data.views.total === 1 ? '' : 's'} by {data.views.people} {data.views.people === 1 ? 'person' : 'people'}</>}
        </p>
        {article.tags.length > 0 && (
          <ul className="da-tags" aria-label="Tags">
            {article.tags.map((t) => <li key={t}><Link to={`/docs?tag=${encodeURIComponent(t)}`} className="da-tag">#{t}</Link></li>)}
          </ul>
        )}
        <div className="da-actions" role="toolbar" aria-label="Article actions">
          {can.edit && <button type="button" className="btn btn-primary btn-small" onClick={() => setMode('edit')}><DocIcon name="edit" />Edit</button>}
          {can.edit && <button type="button" className={`btn btn-small ${mode === 'history' ? 'btn-secondary' : 'btn-outline'}`} onClick={() => setMode(mode === 'history' ? 'read' : 'history')}><DocIcon name="history" />History</button>}
          {can.manage && <button type="button" className="btn btn-outline btn-small" onClick={() => setShare(true)}><DocIcon name="share" />Permissions</button>}
          <button type="button" className={`btn btn-outline btn-small${fav ? ' is-fav' : ''}`} aria-pressed={fav} onClick={toggleFav}><DocIcon name="bookmark" />{fav ? 'Bookmarked' : 'Bookmark'}</button>
          <button type="button" className="btn btn-outline btn-small" onClick={() => window.print()}><DocIcon name="print" />Print</button>
          <button type="button" className="btn btn-plain btn-small" onClick={copyLink}><DocIcon name={copied ? 'check' : 'link'} />{copied ? 'Copied' : 'Copy link'}</button>
          {can.delete && !askArchive && <button type="button" className="btn btn-plain btn-small da-archive" onClick={() => setAskArchive(true)}><DocIcon name="trash" />Archive</button>}
          {askArchive && (
            <span className="da-ask" role="group" aria-label="Archive this article">
              Archive it? An administrator can bring it back.
              <button type="button" className="btn btn-danger btn-small" onClick={archive}>Archive</button>
              <button type="button" className="btn btn-plain btn-small" onClick={() => setAskArchive(false)}>Keep</button>
            </span>
          )}
        </div>
        {err && <p className="dp-error" role="alert">{err}</p>}
      </header>

      {mode === 'history' && can.edit ? <DocHistory id={node.id} onChanged={reload} /> : (
        <div className={`da-layout${toc.length >= 2 ? ' has-toc' : ''}`}>
          {toc.length >= 2 && (
            <nav className="da-toc" aria-label="On this page">
              <details open={typeof window !== 'undefined' && window.innerWidth >= 1100}>
                <summary>On this page</summary>
                <ol>
                  {toc.map((h) => (
                    <li key={h.id} className={`da-toc-${h.level}`}>
                      <a href={`#${h.id}`} onClick={(e) => { const el = document.getElementById(h.id); if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); window.history.replaceState(null, '', `#${h.id}`); } }}>{h.text}</a>
                    </li>
                  ))}
                </ol>
              </details>
            </nav>
          )}
          <div className="da-main">
            {steps > 0 && (
              <div className="da-progress" role="group" aria-label="Your progress">
                <div className="da-progress-text">
                  <strong>{ticks.size} of {steps} steps done</strong>
                  <span>Ticks are kept in this tab only.</span>
                </div>
                <div className="da-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={steps} aria-valuenow={ticks.size}>
                  <span style={{ width: `${Math.round((ticks.size / steps) * 100)}%` }} />
                </div>
                {ticks.size > 0 && <button type="button" className="btn btn-plain btn-small" onClick={() => { setTicks(new Set()); try { sessionStorage.removeItem(tickKey(node.id, article.version)); } catch { /* fine */ } }}>Start again</button>}
              </div>
            )}
            <DocMarkdown blocks={blocks} refs={refs} tasks={tasks} className="da-body" />

            {files.length > 0 && (
              <section className="da-files" aria-label="Files">
                <h2 className="da-side-title">Files</h2>
                <ul>
                  {files.map((f) => (
                    <li key={f.id}>
                      <DocMarkdown text={`[${f.file_name.replace(/[[\]()]/g, '')}](docfile:${f.id})`} className="da-file" />
                      <span className="da-file-meta">{Math.max(1, Math.round(f.size_bytes / 1024))} KB · {f.uploaded_by_name}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <Feedback id={node.id} mine={data.feedback?.mine} onSaved={() => {}} />
            {can.edit && (data.feedback?.yes || data.feedback?.no) ? (
              <p className="da-tally">{data.feedback.yes} found it helpful · {data.feedback.no} did not
                {data.feedback.notes?.length > 0 && <> · latest note: “{data.feedback.notes[0].note}” - {data.feedback.notes[0].person_name}</>}
              </p>
            ) : null}
            <Comments id={node.id} canComment={can.comment} />
          </div>
          <aside className="da-aside">
            {can.edit && <Review id={node.id} review={data.review} onChanged={reload} me={me} />}
            <Cards title="Related" list={data.related} />
            <Cards title="Linked from" list={data.backlinks} />
            <p className="da-level">Your access: <strong>{LEVEL_LABEL[node.level]}</strong>{article.published_at && <> · published {fullDate(article.published_at)}</>}</p>
          </aside>
        </div>
      )}

      {share && (
        <Modal wide title={`Who can see “${article.title}”`} subtitle="Permissions on this article. Changes apply at once." onClose={() => { setShare(false); reload(); }}>
          <DocPermissions nodeId={node.id} kind="article" />
        </Modal>
      )}
    </article>
  );
}
